/**
 * Group connection pool — manages which `useStudyGroupDoSync` hooks
 * are currently mounted.
 *
 * We want "N WebSockets, one per active group" but can't mount a hook
 * conditionally inside a loop (rules of hooks). Instead, the
 * orchestrator renders one `<StudyGroupSubscription groupId={…}>`
 * per group in a stable list, and that component mounts the hook
 * exactly once per group. The pool itself just tracks which groupIds
 * are currently active + applies a short unmount-grace window so
 * that briefly unmounting a dashboard (navigating away) doesn't
 * immediately tear down the WebSocket.
 *
 * Consumer API:
 *   poolAcquire(groupId) → the group's state will stay mounted as
 *     long as at least one consumer holds a ref.
 *   poolRelease(groupId) → decrement refcount; after a grace period
 *     (default 30s) the group unmounts if no one re-acquires.
 *
 * The orchestrator holds exactly one ref per active membership, so
 * in normal use every group the user belongs to stays connected for
 * free. Dashboard batch reads don't need to acquire — they hit the
 * Hono /meta endpoint, not the WebSocket.
 */

import { useSyncExternalStore } from 'react';

type PoolEntry = {
  refCount: number;
  graceTimer?: ReturnType<typeof setTimeout>;
};

const entries = new Map<string, PoolEntry>();
const listeners = new Set<() => void>();

/** Snapshot of active groupIds, in stable insertion order. */
let snapshot: readonly string[] = Object.freeze([]);

function rebuildSnapshot(): void {
  snapshot = Object.freeze([...entries.keys()]);
}

function emit(): void {
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Grace period before a 0-ref group is actually dropped. Tuned for
 * dashboard → group → dashboard navigation without flapping. */
const GRACE_MS = 30_000;

export function poolAcquire(groupId: string): void {
  let entry = entries.get(groupId);
  if (!entry) {
    entry = { refCount: 0 };
    entries.set(groupId, entry);
    rebuildSnapshot();
    emit();
  }
  entry.refCount += 1;
  if (entry.graceTimer) {
    clearTimeout(entry.graceTimer);
    entry.graceTimer = undefined;
  }
}

export function poolRelease(groupId: string): void {
  const entry = entries.get(groupId);
  if (!entry) return;
  entry.refCount = Math.max(0, entry.refCount - 1);
  if (entry.refCount > 0) return;

  if (entry.graceTimer) clearTimeout(entry.graceTimer);
  entry.graceTimer = setTimeout(() => {
    const current = entries.get(groupId);
    if (!current || current.refCount > 0) return;
    entries.delete(groupId);
    rebuildSnapshot();
    emit();
  }, GRACE_MS);
}

/** Hard-drop a group (e.g. on sign-out). */
export function poolDrop(groupId: string): void {
  const entry = entries.get(groupId);
  if (entry?.graceTimer) clearTimeout(entry.graceTimer);
  entries.delete(groupId);
  rebuildSnapshot();
  emit();
}

export function poolDropAll(): void {
  for (const entry of entries.values()) {
    if (entry.graceTimer) clearTimeout(entry.graceTimer);
  }
  entries.clear();
  rebuildSnapshot();
  emit();
}

/** React hook: reactive snapshot of currently-active groupIds. */
export function useActiveGroupIds(): readonly string[] {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );
}
